import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/api/client";
import { useTranslation } from "react-i18next";
import { CheckCheck } from "lucide-react";

const now = new Date();

export default function AttendanceReview() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [status, setStatus] = useState("submitted");
  const [employeeId, setEmployeeId] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selected, setSelected] = useState(new Set());
  const [modal, setModal] = useState(null); // {row, action}
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  const load = () => {
    const q = new URLSearchParams();
    if (status) q.append("status", status);
    if (employeeId) q.append("employee_id", employeeId);
    q.append("year", year);
    q.append("month", month);
    api.get(`/attendance?${q.toString()}`).then((r) => { setItems(r.data); setSelected(new Set()); });
  };

  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, employeeId, year, month]);

  const empMap = useMemo(() => Object.fromEntries(emps.map((e) => [e.id, e])), [emps]);
  const submittedIds = useMemo(() => items.filter((a) => a.status === "submitted").map((a) => a.id), [items]);
  const allSelected = submittedIds.length > 0 && submittedIds.every((id) => selected.has(id));

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(submittedIds));
  };

  const doReview = async (id, action, noteText = "") => {
    await api.post(`/attendance/${id}/review`, { action, note: noteText || null });
    setModal(null); setNote(""); setErr(""); load();
  };

  const openNoteModal = (row, action) => { setModal({ row, action }); setNote(""); setErr(""); };

  const submitNoteModal = () => {
    if (modal.action === "return" && !note.trim()) { setErr(t("common.requiredNote")); return; }
    doReview(modal.row.id, modal.action, note.trim());
  };

  const bulkApprove = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { setToast(t("admin.noSelection")); setTimeout(() => setToast(""), 2000); return; }
    if (!window.confirm(t("admin.confirmBulkApprove", { count: ids.length }))) return;
    const { data } = await api.post("/attendance/bulk-approve", { ids });
    setToast(t("admin.bulkApproveDone", { count: data.approved_count }));
    setTimeout(() => setToast(""), 2500);
    load();
  };

  return (
    <div>
      <h1 className="page-title">{t("admin.attendance")}</h1>
      <div className="row-actions" style={{ marginBottom: 12, alignItems: "center" }}>
        <select className="select" style={{ width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="att-filter-status">
          <option value="">{t("common.all")}</option>
          <option value="submitted">{t("common.submitted")}</option>
          <option value="approved">{t("common.approved")}</option>
          <option value="rejected">{t("common.rejected")}</option>
          <option value="returned">{t("common.returned")}</option>
        </select>
        <select className="select" style={{ width: 220 }} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid="att-filter-emp">
          <option value="">{t("admin.filterEmployee")}</option>
          {emps.map((e) => <option key={e.id} value={e.id}>{e.employee_code} - {e.name}</option>)}
        </select>
        <input className="input" type="number" style={{ width: 100 }} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="att-filter-year" />
        <select className="select" style={{ width: 110 }} value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="att-filter-month">
          {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{i+1}月</option>)}
        </select>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "#64748B" }}>選択: {selected.size} / {submittedIds.length}</span>
          <button className="btn btn-success" onClick={bulkApprove} disabled={selected.size === 0} data-testid="att-bulk-approve">
            <CheckCheck size={14}/> {t("admin.approveSelected")}
          </button>
        </div>
      </div>
      <div className="card"><div className="card-body table-wrap">
        <table className="table" data-testid="att-review-table">
          <thead><tr>
            <th style={{ width: 36 }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="att-select-all" disabled={submittedIds.length === 0} title={t("admin.selectAll")} />
            </th>
            <th>{t("common.date")}</th><th>{t("admin.name")}</th><th>{t("driver.startTime")}</th><th>{t("driver.endTime")}</th>
            <th>{t("driver.breakMinutes")}</th><th>{t("driver.workingHours")}</th><th>{t("common.status")}</th><th>{t("common.actions")}</th>
          </tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} style={selected.has(a.id) ? { background: "#EEF4FF" } : {}}>
                <td>
                  {a.status === "submitted" && (
                    <input
                      type="checkbox"
                      checked={selected.has(a.id)}
                      onChange={() => toggle(a.id)}
                      data-testid={`att-check-${a.id}`}
                    />
                  )}
                </td>
                <td>{a.work_date}</td>
                <td>{empMap[a.employee_id]?.name || "-"}</td>
                <td>{a.start_time || "-"}</td><td>{a.end_time || "-"}</td>
                <td>{a.break_minutes}</td><td>{a.working_hours}</td>
                <td>
                  <span className={`tag tag-${a.status}`}>{t(`common.${a.status}`)}</span>
                  {a.review_note && (a.status === "rejected" || a.status === "returned") && (
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{a.review_note}</div>
                  )}
                </td>
                <td>
                  {a.status === "submitted" ? (
                    <div className="row-actions">
                      <button className="btn btn-success" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => doReview(a.id, "approve")} data-testid={`att-approve-${a.id}`}>{t("common.approve")}</button>
                      <button className="btn btn-danger" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => openNoteModal(a, "reject")} data-testid={`att-reject-${a.id}`}>{t("common.reject")}</button>
                      <button className="btn btn-warning" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => openNoteModal(a, "return")} data-testid={`att-return-${a.id}`}>{t("common.return")}</button>
                    </div>
                  ) : <span style={{ color: "#64748B", fontSize: 12 }}>—</span>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="9" style={{ textAlign: "center", color: "#64748B" }}>{t("driver.noRecords")}</td></tr>}
          </tbody>
        </table>
      </div></div>

      {modal && (
        <div className="modal-backdrop" data-testid="att-note-modal" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-title">
              {modal.action === "return" ? t("common.return") : t("common.reject")} — {modal.row.work_date} / {empMap[modal.row.employee_id]?.name}
            </div>
            <div className="field">
              <label className="label">{t("common.note")} {modal.action === "return" && <span style={{ color: "#DC2626" }}>*</span>}</label>
              <textarea data-testid="att-note-input" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder={modal.action === "return" ? t("common.requiredNote") : ""} />
            </div>
            {err && <div style={{ color: "#DC2626", marginBottom: 8 }} data-testid="att-note-err">{err}</div>}
            <div className="row-actions">
              <button className="btn btn-outline" onClick={() => setModal(null)}>{t("common.cancel")}</button>
              <button className={modal.action === "return" ? "btn btn-warning" : "btn btn-danger"} onClick={submitNoteModal} data-testid="att-note-submit">
                {modal.action === "return" ? t("common.return") : t("common.reject")}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast" data-testid="att-toast">{toast}</div>}
    </div>
  );
}
